/**
 * Shape of the palettes served by `GET /api/design/colorPresets`.
 *
 * The backend is the single source of truth for the palettes themselves — read them
 * through `getColorPresets`. Only the key names live here, because they are also the
 * field names inside `collection.design.newVariables`.
 */
export const DESIGN_COLOR_KEYS = [
  'primaryColor',
  'secondaryColor',
  // "Design elements": filter, selection bars, modals, lightbox and the mobile menu.
  'uiBackgroundColor',
  'uiTextColor',
  'accentColor',
  // Gallery name, date and photographer credit drawn over the cover image, which sit on
  // the photo rather than on the gallery background and so need a colour of their own.
  'coverTextColor'
] as const;

export type DesignColorKey = typeof DESIGN_COLOR_KEYS[number];

export type DesignColors = Partial<Record<DesignColorKey, string>>;

export interface IColorPreset {
  id: string;
  name: string;
  colors: Record<DesignColorKey, string>;
}

/**
 * The palettes a template offers, the same way the backend scopes them: the ones its
 * `colorPresets` lists, in that order, or every palette when it lists none. The first is
 * its default.
 */
export const presetsForTemplate = (
  presets: IColorPreset[],
  template?: { colorPresets?: string[] }
): IColorPreset[] => {
  const ids = template?.colorPresets || [];

  return ids.length ? ids.flatMap((id) => presets.filter((preset) => preset.id === id)) : presets;
};
